import React from 'react'
import { Link } from 'react-router-dom'
import { useUI } from '@/context/UIContext'
import { useCart } from '@/context/CartContext'
import Button from '@/components/UI/Button'

/**
 * MiniCart - Mini cart drawer component
 * 
 * @component
 */
const MiniCart = () => {
  const { modals, closeModal } = useUI()
  const { items, total, itemCount, removeFromCart, updateQuantity } = useCart()

  if (!modals.cartDrawer) return null

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId)
    } else {
      updateQuantity(productId, newQuantity)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => closeModal('cartDrawer')}
      />
      
      {/* Cart drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Shopping Cart ({itemCount})
            </h2>
            <button
              onClick={() => closeModal('cartDrawer')}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* Cart content */}
          <div className="flex-1 overflow-y-auto p-4">
            {items.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-4">🛒</div>
                <p>Your cart is empty</p>
                <p className="text-sm">Add some delicious Mauritian products!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 flex-shrink-0">
                      {item.image && item.image.startsWith('http') ? (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-100 rounded-lg">
                          {item.image || '🛒'}
                        </div>
                      )}
                      <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-100 rounded-lg" style={{ display: 'none' }}>
                        🛒
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                      <div className="flex items-center space-x-1">
                        <p className="text-sm text-primary-600">€{item.price}</p>
                        {item.vat_chargeable && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            VAT {item.vat_rate}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs"
                      >
                        -
                      </button>
                      <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-xs"
                      >
                        +
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-600 hover:text-red-700 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total:</span>
                <span className="text-lg font-bold text-primary-600">€{total.toFixed(2)}</span>
              </div>
              <Link to="/cart" onClick={() => closeModal('cartDrawer')}>
                <Button variant="primary" size="lg" className="w-full">
                  View Cart & Checkout
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => closeModal('cartDrawer')}
              >
                Continue Shopping
              </Button>
            </div>
          )}
          
          {items.length === 0 && (
            <div className="border-t border-gray-200 p-4">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => closeModal('cartDrawer')}
              >
                Continue Shopping
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MiniCart
